/**
 * Google Analytics 4 helper.
 *
 * Wraps window.gtag so all GA calls are safe to make even when the GA
 * script hasn't loaded (e.g. ad-blocked, env var not set) and automatically
 * attaches the association_id dimension from localStorage to every event.
 */

const MEASUREMENT_ID = process.env.VUE_APP_GA_MEASUREMENT_ID;

function gtag(...args) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

/**
 * Returns the current association_id (job board / association source).
 * Reads from localStorage so it is available on every page after being set
 * once from the ?associationId= URL query parameter in App.vue.
 */
function getAssociationId() {
  try {
    return localStorage.getItem('association_id') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Fire a GA4 custom event.
 * association_id is merged automatically from localStorage.
 *
 * @param {string} eventName
 * @param {Object} params
 */
export function trackEvent(eventName, params = {}) {
  if (!MEASUREMENT_ID) return;
  try {
    const association_id = getAssociationId();
    gtag('event', eventName, {
      ...(association_id ? { association_id } : {}),
      ...params,
    });
  } catch (e) {
    console.warn('[GA] trackEvent failed', e);
  }
}

/**
 * Send a manual page_view event for SPA navigation.
 *
 * @param {string} path  - route.fullPath
 * @param {string} title - route name or path
 */
export function trackPageView(path, title) {
  if (!MEASUREMENT_ID) return;
  try {
    gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  } catch (e) {
    console.warn('[GA] trackPageView failed', e);
  }
}

/**
 * Associate the current GA session with an authenticated user.
 *
 * @param {string|number} userId
 * @param {string}        userRole
 */
export function setGAUser(userId, userRole) {
  if (!MEASUREMENT_ID) return;
  try {
    gtag('config', MEASUREMENT_ID, {
      user_id: String(userId),
    });
    gtag('set', 'user_properties', {
      user_role: userRole || undefined,
    });
  } catch (e) {
    console.warn('[GA] setGAUser failed', e);
  }
}

/**
 * Clear user identity on logout.
 */
export function resetGAUser() {
  if (!MEASUREMENT_ID) return;
  try {
    gtag('config', MEASUREMENT_ID, {
      user_id: undefined,
    });
    gtag('set', 'user_properties', {
      user_role: undefined,
    });
  } catch (e) {
    console.warn('[GA] resetGAUser failed', e);
  }
}
